import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { PaginationComponent } from '../pagination/pagination.component';
import { LoaderComponent } from '../layout/loader/loader.component';
import { SearchComponent } from '../search/search.component';
import { NgbActiveModal, NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmDeleteComponent } from '../confirm-delete/confirm-delete.component';
import { Router } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { DutchDatePipe } from '../../pipes/dutch-date.pipe';
import { DutchDateTimePipe } from '../../pipes/dutch-date-time.pipe';
import { StatusPipe } from "../../pipes/status.pipe";

import { CustomCheckboxComponent } from '../custom-checkbox/custom-checkbox.component';

interface TableSection {
  title: string;
  filter?: { field: string; value: any; type: string | null };
  items?: any[];
}

interface CustomActionButton {
  title: string;
  component: any;
  size?: string;
}

interface ColumnConfig {
  key: string;
  cssClass?: string;
  label: string;
  enum?: any;
  type?: string;
}

@Component({
  selector: 'app-table-guru',
  templateUrl: './table-guru.component.html',
  standalone: true,
  imports: [
    LoaderComponent,
    PaginationComponent,
    SearchComponent,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownItem,
    NgbDropdownToggle,
    CommonModule,
    FormsModule,
    DutchDateTimePipe,
    DutchDatePipe,
    StatusPipe,
    CustomCheckboxComponent
  ],
  styleUrls: ['./table-guru.component.scss'],
})
export class TableGuruComponent implements OnInit {
  @Input() apiEndpoint!: string;
  @Input() columnMapping!: ColumnConfig[];
  @Input() itemsPerPage: number = 1;
  @Input() openModalComponent: any;
  @Input() viewURL: any;
  @Input() reload: boolean = false;
  @Input() customActionButtons: CustomActionButton[] = [];
  @Input() sections: TableSection[] = [];

  @Input() defaultFilters: { [key: string]: any } = {};

  @Input() title: string = '';

  @Input() actionButtonText: string = '';
  @Input() showActionButton: boolean = false;
  @Input() enableFilters: boolean = false;
  @Output() actionButtonClicked = new EventEmitter<boolean>();

  items: any[] = [];
  totalItems: number = 0;
  lastPage: number = 0;
  currentPage: number = 1;
  searchQuery: string = '';
  sortField: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  loading: boolean = false;

  sectionedData: { [key: string]: any[] } = {};

  filtersData: any = {};
  filters: { [key: string]: any } = {};

  private modalService = inject(NgbModal);

  constructor(
    private apiService: ApiService,
    private router: Router
  ) { }

  ngOnInit() {
    if (!this.apiEndpoint || !this.columnMapping) {
      console.error('TableGuruComponent: apiEndpoint and columnMapping are required inputs.');
      return;
    }
    this.fetchData();
    this.getFilters();
  }

  onButtonClick() {
    this.actionButtonClicked.emit(true);
  }

  getActiveFilterCount() {
    let total = 0;

    for (const key in this.filters) {
      if (Array.isArray(this.filters[key])) {
        total += this.filters[key].length;
      } else if (this.filters[key] == true) {
        total++;
      }
    }

    return total;
  }

  clearFilterSection(filterKey: string) {
    if (this.filtersData[filterKey].type === 'select') {
      this.filters[filterKey] = [];
    } else {
      this.filters[filterKey] = null;
    }
    this.applyFilters();
  }

  getActiveFilters() {
    const groupedFilters: { [key: string]: { values: string[], key: string } } = {};

    for (const filterKey in this.filters) {
      if (this.filters.hasOwnProperty(filterKey)) {
        const filterValue = this.filters[filterKey];
        const filterData = this.filtersData[filterKey];

        if (filterValue && filterData) {
          const label = filterData.label;

          if (!groupedFilters[label]) {
            groupedFilters[label] = {
              values: [],
              key: filterKey
            };
          }

          if (Array.isArray(filterValue)) {
            filterValue.forEach(val => {
              const displayLabel = filterData.values && filterData.values[val] ? filterData.values[val] : val;
              groupedFilters[label].values.push(displayLabel);
            });
          } else if (typeof filterValue === 'boolean' && filterValue === true) {
            groupedFilters[label].values.push('Ja');
          } else if (typeof filterValue === 'string' && filterValue.length > 0) {
            groupedFilters[label].values.push(filterValue);
          }
        }
      }
    }
    return groupedFilters;
  }

  removeTag(filterKey: string, filterValue: string) {
    const filterData = this.filtersData[filterKey];

    if (filterData.type === 'select') {
      const optionKey = Object.keys(filterData.values).find(key => filterData.values[key] === filterValue || key === filterValue);
      if (optionKey) {
        const index = this.filters[filterKey].indexOf(optionKey);
        if (index > -1) {
          this.filters[filterKey].splice(index, 1);
        }
      }
    } else if (filterData.type === 'boolean') {
      this.filters[filterKey] = false;
    } else {
      this.filters[filterKey] = null;
    }

    this.applyFilters();
  }

  getFilterLabels(): string[] {
    return Object.keys(this.getActiveFilters());
  }

  // New method to apply filters
  applyFilters() {
    this.currentPage = 1;
    this.fetchData();
  }

  // New method to clear filters
  clearFilters() {
    for (const key in this.filters) {
      if (this.filters.hasOwnProperty(key)) {
        if (this.filtersData[key].type === 'select') {
          this.filters[key] = [];
        } else {
          this.filters[key] = null;
        }
      }
    }
    this.currentPage = 1;
    this.fetchData();
  }

  // New methods for multi-select
  toggleOption(filterKey: string, optionKey: string, isChecked: boolean) {
    if (!Array.isArray(this.filters[filterKey])) {
      this.filters[filterKey] = [];
    }
    if (isChecked) {
      this.filters[filterKey].push(optionKey);
    } else {
      const index = this.filters[filterKey].indexOf(optionKey);
      if (index > -1) {
        this.filters[filterKey].splice(index, 1);
      }
    }
    this.applyFilters();
  }

  isOptionSelected(filterKey: string, optionKey: string): boolean {
    if (!Array.isArray(this.filters[filterKey])) {
      return false;
    }
    return this.filters[filterKey].includes(optionKey);
  }

  formatToDutchDate(isoDateString: any) {
    if (isoDateString == null) {
      return "-";
    } else {
      const date = new Date(isoDateString);

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    }
  }

  formatToDutchDateTime(isoDateString: any) {
    if (!isoDateString) {
      return "-";
    }

    const date = new Date(isoDateString);

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }


  formatToDutchEuro(value: any): string {
    const formatter = new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(value);
  }

  ngOnChanges(changes: any) {
    if (this.reload === true) {
      this.fetchData();
      this.reload = false;
    }
  }

  limitEmail(email: string) {
    const [name, domain] = email.split('@');
    const maxNameLength = 56 - (domain.length + 1);
    const shortName = name.length > maxNameLength
      ? name.slice(0, maxNameLength) + '...'
      : name;
    return `${shortName}@${domain}`;
  }

  openPage(item: any) {
    if (this.viewURL) {
      this.router.navigate([this.viewURL + item['id']]);
    }
  }

  sortKey(key: string) {
    if (this.sortField == key) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = key;
      this.sortOrder = 'asc';
    }

    this.fetchData();
  }

  getFilters() {
    if (!this.enableFilters) { return; }
    this.apiService
      .get(this.apiEndpoint + "/filters")
      .then((response: any) => {
        this.filtersData = response;
        this.filters = {};

        for (const key in this.filtersData) {
          if (this.filtersData.hasOwnProperty(key)) {
            if (this.filtersData[key].type === 'select') {
              this.filters[key] = [];
            } else {
              this.filters[key] = null;
            }
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching filters:', error);
      });
  }

  getFilterKeys(): string[] {
    return Object.keys(this.filtersData);
  }

  getOptionKeys(values: any): string[] {
    if (values) {
      return Object.keys(values);
    } else {
      return [];
    }
  }

  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  fetchData() {
    this.loading = true;

    const params: { [key: string]: any } = {
      page: this.currentPage,
      limit: this.itemsPerPage,
    };

    if (this.sortField) {
      params['sort'] = this.sortField;
      params['order'] = this.sortOrder;
    }

    if (this.searchQuery) {
      params['keyword'] = this.searchQuery;
    }

    for (const key in this.filters) {
      if (this.filters.hasOwnProperty(key) && this.filters[key] !== null && this.filters[key] !== '' && (!Array.isArray(this.filters[key]) || this.filters[key].length > 0)) {
        if (Array.isArray(this.filters[key])) {
          params[`filter[${key}]`] = this.filters[key].join(',');
        } else if (typeof this.filters[key] === 'boolean') {
          params[`filter[${key}]`] = this.filters[key] ? 'true' : 'false';
        } else {
          params[`filter[${key}]`] = this.filters[key];
        }
      }
    }

    this.apiService
      .get(this.apiEndpoint, params)
      .then((response: any) => {
        this.items = response.data;
        this.totalItems = response.total;
        this.lastPage = response.last_page;

        if (this.sections.length > 0) {
          this.processSections();
        }

        this.loading = false;
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        this.loading = false;
      });
  }

  processSections() {
    this.sectionedData = {};

    const allItems = [...this.items];

    this.sections.forEach(section => {
      const sectionKey = section.title || 'default';

      if (section.filter) {
        if (section.filter['type'] == 'not') {
          this.sectionedData[sectionKey] = allItems.filter(item => {
            const itemValue = this.getNestedValue(item, section.filter!.field);
            return section.filter!.value != itemValue;
          });
        } else {
          this.sectionedData[sectionKey] = allItems.filter(item => {
            const itemValue = this.getNestedValue(item, section.filter!.field);
            return section.filter!.value.includes(itemValue);
          });
        }
      } else {
        this.sectionedData[sectionKey] = [...allItems];
      }
    });

    if (Object.keys(this.sectionedData).length === 0 && this.sections.length > 0) {
      this.sectionedData['default'] = allItems;
    }
  }

  getItemsForSection(sectionTitle: string): any[] {
    if (this.sections.length === 0) {
      return this.items;
    }

    return this.sectionedData[sectionTitle] || [];
  }

  handlePageChange(newPage: number) {
    this.currentPage = newPage;
    this.fetchData();
  }

  handleSearch(query: string) {
    this.searchQuery = query;
    this.currentPage = 1;
    this.fetchData();
  }

  handleSort(field: string) {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortOrder = 'asc';
    }
    this.fetchData();
  }

  handleComponent(customActionButton: CustomActionButton, item: any) {
    let component = customActionButton.component;
    let size = customActionButton.size ?? "lg";
    const modalRef = this.modalService.open(component, {
      size: size,
    });
    modalRef.componentInstance.modalStyle = true;
    modalRef.componentInstance.item = item;
    modalRef.result.then(() => {
      this.fetchData();
    });
  }

  openModal(item: any) {
    const modalRef = this.modalService.open(this.openModalComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.modalStyle = true;
    modalRef.componentInstance.item = item;
    modalRef.result.then(() => {
      this.fetchData();
    });
  }

  delete(item: any) {
    const deleteModal = this.modalService.open(ConfirmDeleteComponent);
    deleteModal.componentInstance.message = 'Weet je zeker dat je wilt verwijderen?';
    deleteModal.result.then((result) => {
      if (result) {
        this.apiService
          .delete(`${this.apiEndpoint}/${item.id}`)
          .then(() => this.fetchData())
          .catch(() => this.fetchData());
      }
    });
  }
}